/** OfficeFloor — new expanded Virtual Production Office renderer.
 *
 *  Renders the full ~45-cubicle catalog grouped into pods (Direction,
 *  Camera Crew, Light, Grip, Sound, Art, Wardrobe, H&M, Casting,
 *  Actors, Stunt, Post, Location, Vendor, Craft, Safety, Specialty).
 *
 *  Locked cubicles remain visible but rendered in low-opacity so users
 *  still see the shape of the entire office floor. Clicking an unlocked
 *  tile deep-links to /projects/:id/cubicles?cubicle=<key>.
 *
 *  Compound cubicles (currently just ``actors``) expand into their own
 *  sub-cubicle strip with add/remove controls for authorised roles.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Lock, Plus, Trash2, X } from "lucide-react";

export default function OfficeFloor({ projectId, access }) {
  const nav = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [compound, setCompound] = useState({});
  const [addFor, setAddFor] = useState(null);
  const [addLabel, setAddLabel] = useState("");

  useEffect(() => {
    api.get("/office-floor/catalog")
       .then(({ data }) => setCatalog(data))
       .catch(() => setCatalog({ pods: [], cubicles: [], compound_defaults: {} }));
    api.get(`/projects/${projectId}/compound-cubicles`)
       .then(({ data }) => setCompound(data.items || {}))
       .catch(() => setCompound({}));
  }, [projectId]);

  const pods = useMemo(() => {
    if (!catalog) return [];
    const byPod = {};
    (catalog.cubicles || []).forEach((c) => {
      (byPod[c.pod] = byPod[c.pod] || []).push(c);
    });
    return (catalog.pods || []).map((p) => ({
      ...p,
      cubicles: byPod[p.key] || [],
    })).filter((p) => p.cubicles.length > 0);
  }, [catalog]);

  const isMaster = !!access?.master_key;

  const openCubicle = (key) => {
    if (!access?.cubicle_access?.[key]) return;
    nav(`/projects/${projectId}/cubicles?cubicle=${encodeURIComponent(key)}`);
  };

  const submitAdd = async () => {
    if (!addLabel.trim() || !addFor) return;
    try {
      const { data } = await api.post(
        `/projects/${projectId}/compound-cubicles/${addFor}`,
        { label: addLabel.trim() },
      );
      setCompound({
        ...compound,
        [addFor]: [...(compound[addFor] || []), data.row],
      });
      toast.success(`Added ${data.row.label}`);
      setAddFor(null); setAddLabel("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    }
  };

  const removeSub = async (parentKey, subId) => {
    try {
      await api.delete(
        `/projects/${projectId}/compound-cubicles/${parentKey}/${encodeURIComponent(subId)}`);
      setCompound({
        ...compound,
        [parentKey]: (compound[parentKey] || []).filter((r) => r.id !== subId),
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    }
  };

  if (!catalog) {
    return <div className="p-10 text-white/45 text-sm">Loading office floor…</div>;
  }

  return (
    <div className="mt-8 space-y-10" data-testid="office-floor">
      {pods.map((pod) => (
        <section key={pod.key} data-testid={`office-pod-${pod.key}`}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-2 h-6 rounded-full" style={{background: pod.accent}}/>
            <h3 className="font-display text-xl uppercase tracking-wide">{pod.label}</h3>
            <div className="text-[10px] tracking-[0.22em] uppercase text-white/45">
              {pod.cubicles.length} {pod.cubicles.length === 1 ? "cubicle" : "cubicles"}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2.5">
            {pod.cubicles.map((c) => {
              const unlocked = !!access?.cubicle_access?.[c.key];
              const isCompound = c.compound;
              const subs = compound[c.key] || [];
              return (
                <div key={c.key}
                     className="flex flex-col"
                     data-testid={`office-cubicle-${c.key}`}>
                  <button
                    type="button"
                    onClick={() => openCubicle(c.key)}
                    disabled={!unlocked}
                    className={"cs-card rounded-xl p-3 text-left transition group relative overflow-hidden "
                             + (unlocked ? "hover:border-white/30 cursor-pointer" : "opacity-30 cursor-not-allowed")}
                    style={{borderTop: `2px solid ${pod.accent}`}}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-[8px] tracking-[0.24em] uppercase"
                             style={{color: pod.accent}}>
                          {c.category === "vendor" ? "Vendor" : "Crew"}
                        </div>
                        <div className="font-display text-[13px] leading-tight mt-1 line-clamp-2">
                          {c.label}
                        </div>
                      </div>
                      {!unlocked && <Lock size={11} className="text-white/40 shrink-0 mt-1"/>}
                    </div>
                  </button>
                  {/* Compound sub-cubicles strip */}
                  {isCompound && (
                    <div className="mt-2 space-y-1"
                         data-testid={`compound-subs-${c.key}`}>
                      {subs.map((s) => (
                        <div key={s.id}
                             className="flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-md bg-white/[0.03] border border-white/8"
                             data-testid={`compound-sub-${s.id}`}>
                          <div className="w-1 h-3 rounded-full" style={{background: pod.accent, opacity: 0.6}}/>
                          <div className="flex-1 text-[11px] truncate">{s.label}</div>
                          {s.removable !== false && isMaster && (
                            <button onClick={() => removeSub(c.key, s.id)}
                                    className="text-white/35 hover:text-red-400"
                                    data-testid={`compound-sub-remove-${s.id}`}>
                              <Trash2 size={10}/>
                            </button>
                          )}
                        </div>
                      ))}
                      {isMaster && (
                        <button onClick={() => { setAddFor(c.key); setAddLabel(""); }}
                                className="w-full flex items-center gap-1.5 justify-center py-1.5 rounded-md border border-dashed border-white/15 text-[10px] tracking-[0.18em] uppercase text-white/50 hover:text-white hover:border-white/30"
                                data-testid={`compound-add-${c.key}`}>
                          <Plus size={10}/> Add
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      ))}

      {/* Add sub-cubicle modal */}
      {addFor && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={() => setAddFor(null)}
             data-testid="add-compound-sub-modal">
          <div onClick={(e) => e.stopPropagation()}
               className="bg-neutral-950 border border-white/12 rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">Add sub-cubicle</div>
                <div className="font-display text-2xl mt-1 capitalize">{addFor}</div>
              </div>
              <button onClick={() => setAddFor(null)} className="text-white/50"><X size={16}/></button>
            </div>
            <input value={addLabel}
                   onChange={(e) => setAddLabel(e.target.value)}
                   placeholder="e.g. Shah Rukh Khan · lead artist"
                   className="mt-4 w-full bg-black/50 border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white/90"
                   data-testid="add-compound-sub-input"
                   autoFocus/>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setAddFor(null)}
                      className="px-4 py-2 text-sm text-white/60">Cancel</button>
              <button onClick={submitAdd}
                      className="btn-themed h-10 px-5 rounded-full uppercase tracking-[0.2em] text-[11px]"
                      data-testid="add-compound-sub-submit">
                Add cubicle
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
